import { useState } from "react";
import { Question } from "@/components/QuizGame";

interface QuestionCardProps {
  question: Question;
  questionNumber: number;
  totalQuestions: number;
  timeLeft: number;
  onAnswer: (chosenIndex: number) => void;
}

const QuestionCard = ({
  question,
  questionNumber,
  totalQuestions,
  timeLeft,
  onAnswer,
}: QuestionCardProps) => {
  const [selected, setSelected] = useState<number | null>(null);

  const handleSelect = (index: number) => {
    if (selected !== null) return;
    setSelected(index);
    onAnswer(index);
  };

  const correctIndex = question.correctIndex;

  const getOptionClass = (index: number) => {
    const base =
      "w-full text-left px-5 py-4 rounded-lg border font-body text-base transition-all duration-200 cursor-pointer";
    if (selected === null)
      return `${base} border-border bg-secondary/50 hover:bg-secondary hover:border-primary/40`;
    if (index === correctIndex)
      return `${base} border-green-500 bg-green-500/15 text-green-400`;
    if (index === selected)
      return `${base} border-red-500 bg-red-500/15 text-red-400`;
    return `${base} border-border bg-secondary/30 opacity-40`;
  };

  return (
    <div className="w-full max-w-lg animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <span className="text-muted-foreground text-sm font-heading">
          Question {questionNumber}/{totalQuestions}
        </span>
        <div className="flex items-center gap-3 flex-1 ml-4">
          <div className="min-w-12 text-right text-sm font-heading text-primary tabular-nums">
            {timeLeft}s
          </div>
          <div className="h-1.5 flex-1 bg-secondary rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${(questionNumber / totalQuestions) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <h2 className="font-heading text-xl sm:text-2xl font-semibold mb-6 leading-snug">
        {question.question}
      </h2>

      <div className="space-y-3">
        {question.options.map((option, i) => (
          <button
            key={i}
            className={getOptionClass(i)}
            onClick={() => handleSelect(i)}
            disabled={selected !== null}
          >
            <span className="text-muted-foreground mr-3 font-heading text-sm">
              {String.fromCharCode(65 + i)}
            </span>
            {option}
          </button>
        ))}
      </div>
    </div>
  );
};

export default QuestionCard;
